import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Paper,
  Fade
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Label as LabelIcon,
  Info as InfoIcon
} from '@material-ui/icons'
import { toast } from 'utils/notify'
import { addPostTopic, deletePostTopic, updatePostTopic } from 'store/community'
import moment from 'moment/moment'
import { useDispatch } from 'react-redux'

const ManageTopicsModal = ({ open, onClose, conferenceId, topics }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [newTopicTitle, setNewTopicTitle] = useState('')
  const [editingTopic, setEditingTopic] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [deletedTopicId, setDeletedTopicId] = useState('')

  // Effect to refresh topics after changes
  useEffect(() => {
    if (conferenceId && open) {
      // When modal opens, reset states
      setNewTopicTitle('')
      setEditingTopic(null)
      setEditTitle('')
    }
  }, [conferenceId, open])

  // Function to add a new topic
  const handleAddTopic = async () => {
    if (!newTopicTitle.trim()) {
      toast.error('Topic title is required.')
      return
    }

    if (newTopicTitle.trim().length > 50) {
      toast.error('Topic title must be 50 characters or fewer.')
      return
    }

    // Check if topic with same title already exists
    const exists = topics.some(
      (topic) => topic.title.toLowerCase() === newTopicTitle.trim().toLowerCase()
    )
    if (exists) {
      toast.error('A topic with this title already exists')
      return
    }

    setIsAdding(true)
    try {
      await dispatch(
        addPostTopic(
          {
            title: newTopicTitle.trim(),
            conferenceId,
            createdAt: moment().utc().valueOf()
          },
          conferenceId
        )
      )
      setNewTopicTitle('')
      toast.success('Topic added.')
    } catch (error) {
      console.error('Error adding topic:', error)
      toast.error('Could not add topic.')
    } finally {
      setIsAdding(false)
    }
  }

  // Function to start editing a topic
  const handleStartEdit = (topic) => {
    setEditingTopic(topic)
    setEditTitle(topic.title)
  }

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingTopic(null)
    setEditTitle('')
  }

  // Function to save edited topic
  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      toast.error('Topic title is required.')
      return
    }

    if (editTitle.trim().length > 50) {
      toast.error('Topic title must be 50 characters or fewer.')
      return
    }

    // Check if topic with same title already exists (excluding the current one)
    const trimmedEditTitle = editTitle.trim().toLowerCase()
    const exists = topics.some(
      (topic) => topic.id !== editingTopic.id && topic.title.toLowerCase() === trimmedEditTitle
    )
    if (exists) {
      toast.error('A topic with this title already exists')
      return
    }

    setIsEditing(true)
    try {
      await dispatch(
        updatePostTopic(editingTopic.id, {
          title: editTitle.trim(),
          updatedAt: moment.utc().valueOf()
        })
      )
      setEditingTopic(null)
      setEditTitle('')
      toast.success('Topic updated.')
    } catch (error) {
      console.error('Error updating topic:', error)
      toast.error('Could not update topic.')
    } finally {
      setIsEditing(false)
    }
  }

  // Function to delete a topic
  const handleDeleteTopic = async (topicId) => {
    setDeletedTopicId(topicId)
    try {
      await dispatch(deletePostTopic(topicId))
    } catch (error) {
      console.error('Error deleting topic:', error)
      toast.error('Could not delete topic.')
    } finally {
      setDeletedTopicId('')
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      aria-labelledby="manage-topics-dialog-title"
      PaperProps={{
        elevation: 3,
        style: { borderRadius: 12 }
      }}>
      <DialogTitle id="manage-topics-dialog-title" className={classes.dialogTitle}>
        <Typography variant="h6">Manage Community Topics</Typography>
        <IconButton
          size="small"
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Paper elevation={0} className={classes.topicForm}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Enter Topic Title"
            value={newTopicTitle}
            className={classes.topicField}
            onChange={(e) => setNewTopicTitle(e.target.value)}
            disabled={isAdding}
            size="small"
            InputProps={{
              endAdornment: isAdding ? <CircularProgress size={20} color="primary" /> : null
            }}
            helperText={`${newTopicTitle.length}/50 characters`}
            error={newTopicTitle.length > 50}
          />
          <Box>
            <Button
              variant="contained"
              color="primary"
              className={classes.addButton}
              startIcon={<AddIcon />}
              onClick={handleAddTopic}
              disabled={isAdding || !newTopicTitle.trim() || newTopicTitle.trim().length > 50}>
              Add
            </Button>
          </Box>
        </Paper>

        <Box className={classes.topicsHeader}>
          <Typography variant="subtitle1">Current Topics</Typography>
          <Typography variant="body2" className={classes.topicsCount}>
            {topics.length} {topics.length === 1 ? 'topic' : 'topics'}
          </Typography>
        </Box>

        {topics.length === 0 ? (
          <Box className={classes.emptyState}>
            <LabelIcon fontSize="large" color="disabled" style={{ marginBottom: 16 }} />
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No topics have been created yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Add your first topic using the form above to help categorize community posts
            </Typography>
          </Box>
        ) : (
          <Paper elevation={0} className={classes.topicsContainer}>
            {topics.map((topic) =>
              editingTopic && editingTopic.id === topic.id ? (
                <Fade in key={topic.id}>
                  <Paper elevation={2} className={classes.editForm}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      disabled={isEditing}
                      size="small"
                      autoFocus
                      placeholder="Topic title"
                      InputProps={{
                        endAdornment: isEditing ? (
                          <CircularProgress size={20} color="primary" />
                        ) : null
                      }}
                      helperText={`${editTitle.length}/50 characters`}
                      error={editTitle.length > 50}
                    />
                    <Box className={classes.editActions}>
                      <Tooltip title="Save">
                        <IconButton
                          color="primary"
                          size="small"
                          className={classes.confirmButton}
                          onClick={handleSaveEdit}
                          disabled={isEditing || !editTitle.trim() || editTitle.trim().length > 50}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          className={classes.cancelButton}
                          onClick={handleCancelEdit}
                          disabled={isEditing}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                </Fade>
              ) : (
                <Chip
                  key={topic.id}
                  label={topic.title}
                  variant="outlined"
                  className={classes.chip}
                  deleteIcon={
                    deletedTopicId && deletedTopicId === topic?.id ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DeleteIcon fontSize="small" color="inherit" />
                    )
                  }
                  onDelete={() => handleDeleteTopic(topic?.id)}
                  onClick={() => handleStartEdit(topic)}
                  disabled={deletedTopicId || Boolean(editingTopic)}
                />
              )
            )}
          </Paper>
        )}

        <Paper elevation={0} className={classes.infoText}>
          <Box display="flex" alignItems="center">
            <InfoIcon fontSize="small" style={{ marginRight: 8 }} />
            <Typography variant="caption" component="p">
              <strong>Note:</strong> Topics used in posts cannot be deleted.
            </Typography>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  )
}

export default ManageTopicsModal

const useStyles = makeStyles((theme) => ({
  addButton: {
    width: '120px',
    height: '46px'
  },
  dialogContent: {
    minWidth: 500,
    paddingBottom: theme.spacing(3)
  },
  topicForm: {
    display: 'flex',
    padding: '10px',
    marginBottom: theme.spacing(3),
    gap: theme.spacing(1)
  },
  topicsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    minHeight: 100
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  chip: {
    margin: theme.spacing(0.5),
    padding: theme.spacing(2, 0),
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    borderColor: theme.palette.primary.light,
    '&:hover': {
      boxShadow: theme.shadows[2],
      backgroundColor: `${theme.palette.primary.light} !important`,
      color: theme.palette.common.white,
      borderColor: theme.palette.primary.light
    }
  },
  editActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  confirmButton: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.success.main
    }
  },
  cancelButton: {
    backgroundColor: theme.palette.grey[300],
    '&:hover': {
      backgroundColor: theme.palette.grey[400]
    }
  },
  editForm: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper
  },
  infoText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `4px solid ${theme.palette.primary.main}`
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    minHeight: 200
  },
  topicsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 1)
  },
  topicsCount: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: '0.875rem',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius
  },
  dialogTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2)
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  topicField: {
    '& .MuiInputBase-root': {
      height: '46px'
    }
  }
}))
